import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import axiosInstance from '../utils/axiosInstance';
import StudentLayout from '../components/StudentLayout';
import AuthService from '../services/authService';
import { getImageURL } from '../services/GeneralServices';

const Wrapper = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const Heading = styled.h2`
  font-size: 24px;
  margin-bottom: 8px;
  font-weight: 700;
  color: #111827;
`;

const Subtitle = styled.p`
  margin: 0 0 24px;
  color: #6b7280;
`;

const StudentCard = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  margin-bottom: 24px;
`;

const Avatar = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 999px;
  background: #1f2937;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 20px;
`;

const StudentInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Name = styled.div`
  font-weight: 600;
  color: #111827;
`;

const Meta = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

const CoursesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
`;

const CourseCard = styled.button`
  text-align: left;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid ${props => (props.$selected ? '#4f46e5' : '#e5e7eb')};
  background: ${props => (props.$selected ? '#eef2ff' : '#ffffff')};
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  &:hover {
    border-color: #4f46e5;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
  }
`;

const CourseName = styled.div`
  font-weight: 600;
  color: #111827;
`;

const CourseMeta = styled.div`
  margin-top: 4px;
  font-size: 13px;
  color: #6b7280;
`;

const StatusRow = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #4b5563;
`;

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: ${props => props.$color || '#22c55e'};
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.15);
`;

const ProgressWrapper = styled.div`
  margin-top: 12px;
`;

const ProgressTrack = styled.div`
  position: relative;
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  border-radius: 999px;
  background: ${props =>
    props.$completed
      ? '#22c55e'
      : 'linear-gradient(90deg, #22c55e, #eab308, #ef4444)'};
  width: ${props => `${props.$value || 0}%`};
  transition: width 0.25s ease-out;
`;

const ProgressLabel = styled.div`
  margin-top: 4px;
  font-size: 11px;
  color: #6b7280;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
`;

const Actions = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
`;

const ContinueButton = styled.button`
  min-width: 140px;
  height: 40px;
  border-radius: 999px;
  border: none;
  background: #111827;
  color: white;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  padding: 0 20px;
  transition: background 0.15s, transform 0.02s, box-shadow 0.15s;
  &:hover {
    background: #020617;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.20);
  }
  &:active {
    transform: translateY(0.5px);
  }
`;

const EmptyState = styled.div`
  padding: 24px;
  border-radius: 12px;
  background: #f9fafb;
  border: 1px dashed #e5e7eb;
  color: #6b7280;
`;

const LoadingText = styled.div`
  margin-top: 8px;
  color: #6b7280;
`;

export default function SelectCourse() {
  const [loading, setLoading] = useState(true);
  const [student, setStudent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axiosInstance
      .get('/students/me')
      .then((resp) => {
        if (cancelled) return;
        const data = resp?.data || {};
        setStudent(data.student || null);
        setRegistrations(Array.isArray(data.registrations) ? data.registrations : []);
        // Try to restore previously selected registration
        try {
          const stored = AuthService.getUser();
          const regId = stored?.selectedRegistration?.registrationId;
          if (regId) {
            setSelectedId(regId);
          }
        } catch (_) {}
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to fetch student profile', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onContinue = () => {
    const selected = registrations.find((r) => r.registrationId === selectedId);
    if (!selected) return;

    // Store selected registration in encrypted local storage payload
    try {
      const current = AuthService.getUser() || {};
      const next = {
        ...current,
        selectedRegistration: selected,
      };
      // AuthService doesn't expose a setter, so mirror what Login.jsx does
      const { encryptToUrl } = require('../utils/crypto');
      const encryptedUrl = encryptToUrl(next);
      const valueToStore = `kaarya://${encryptedUrl}`;
      window.localStorage.setItem('kaarya_user', valueToStore);
      window.location.replace('/app/dashboard');
    } catch (err) {
      console.error('Failed to persist selected registration', err);
      window.location.replace('/app/dashboard');
    }
  };

  const initials = (student?.fullName || '').split(' ').filter(Boolean).map((s) => s[0]).join('').slice(0, 2).toUpperCase();

  const computeProgress = (reg) => {
    const status = String(reg.batchStatus || '').toLowerCase();
    if (status === 'delivery completed' || status === 'batch completed') {
      return 100;
    }

    const startStr = reg.batchStartingDate;
    const endStr = reg.batchEndingDate;

    if (!startStr || !endStr) return 0;

    const start = new Date(startStr);
    const end = new Date(endStr);
    const now = new Date();

    if (!(start instanceof Date && !isNaN(start)) || !(end instanceof Date && !isNaN(end))) {
      return 0;
    }

    if (now <= start) return 0;
    if (now >= end) return 100;

    const total = end.getTime() - start.getTime();
    if (total <= 0) return 0;

    const elapsed = now.getTime() - start.getTime();
    return Math.max(0, Math.min(100, Math.round((elapsed / total) * 100)));
  };

  const getStatusMeta = (reg) => {
    const {
      registrationStatus,
      deliveryStatus,
      universityID,
      universityRegisterDate,
    } = reg || {};

    const isCompleted = !!deliveryStatus && [
      'academic delivery completed',
      'graduated',
      'certificate received',
      'certificate dispatched',
    ].includes(String(deliveryStatus));

    const isUniversityRegistered = !!(universityID || universityRegisterDate);

    let color = '#22c55e';
    let label = 'Active registration';

    if (isCompleted) {
      color = '#6b7280';
      label = 'Delivery completed';
    } else if (!isUniversityRegistered) {
      color = '#f97316';
      label = 'Active · Uni registration pending';
    } else {
      color = '#22c55e';
      label = 'Active · University registered';
    }

    const universityBits = [];
    if (universityID) universityBits.push(`Uni ID: ${universityID}`);
    if (universityRegisterDate) universityBits.push(`Uni reg: ${universityRegisterDate}`);

    const deliveryLabel = deliveryStatus ? `Delivery: ${deliveryStatus}` : null;

    return {
      color,
      label,
      detail: [universityBits.join(' · '), deliveryLabel].filter(Boolean).join(' · '),
    };
  };

  return (
    <StudentLayout title="Select course" requireSelection={false}>
      <Wrapper>
        <Heading>Select your course</Heading>
        <Subtitle>
          Choose which registered course you want to view now. You can switch later if needed.
        </Subtitle>

        {student && (
          <StudentCard>
            {student.imagePath ? (
              <img
                src={getImageURL(student.imagePath)}
                alt={student.fullName || 'Student'}
                style={{ width: 56, height: 56, borderRadius: '999px', objectFit: 'cover' }}
              />
            ) : (
              <Avatar>{initials || 'ST'}</Avatar>
            )}
            <StudentInfo>
              <Name>{student.fullName || 'Student'}</Name>
              <Meta>
                {student.studentId && <>ID: {student.studentId}&nbsp;&nbsp;·&nbsp;&nbsp;</>}
                {student.email || student.username}
              </Meta>
            </StudentInfo>
          </StudentCard>
        )}

        {loading && <LoadingText>Loading your courses…</LoadingText>}

        {!loading && registrations.length === 0 && (
          <EmptyState>
            We couldn't find any active course registrations for your account.
            Please contact the administrator if you believe this is a mistake.
          </EmptyState>
        )}

        {!loading && registrations.length > 0 && (
          <>
            <CoursesGrid>
              {registrations.map((reg) => (
                <CourseCard
                  key={reg.registrationId}
                  type="button"
                  $selected={selectedId === reg.registrationId}
                  onClick={() => setSelectedId(reg.registrationId)}
                >
                  <CourseName>{reg.courseName || reg.courseShortName}</CourseName>
                  <CourseMeta>
                    {reg.courseShortName && <>{reg.courseShortName} · </>}
                    Reg No: {reg.registrationNumber}
                    {reg.batchName && <> · Batch: {reg.batchName}</>}
                  </CourseMeta>

                  <StatusRow>
                    {(() => {
                      const meta = getStatusMeta(reg);
                      return (
                        <>
                          <StatusDot $color={meta.color} />
                          <span>{meta.label}</span>
                          {meta.detail && (
                            <span style={{ color: '#9ca3af' }}>· {meta.detail}</span>
                          )}
                        </>
                      );
                    })()}
                  </StatusRow>

                  <ProgressWrapper>
                    <ProgressTrack>
                      <ProgressFill
                        $value={computeProgress(reg)}
                        $completed={
                          String(reg.batchStatus || '').toLowerCase() === 'delivery completed' ||
                          String(reg.batchStatus || '').toLowerCase() === 'batch completed'
                        }
                      />
                    </ProgressTrack>
                    <ProgressLabel>
                      <span>
                        Batch progress: {computeProgress(reg)}%
                        {reg.batchStatus ? ` · ${reg.batchStatus}` : ''}
                      </span>
                      <span>
                        {reg.whatsappGroupName
                          ? `WhatsApp: ${reg.whatsappGroupName}`
                          : 'WhatsApp group not set'}
                      </span>
                    </ProgressLabel>
                  </ProgressWrapper>
                </CourseCard>
              ))}
            </CoursesGrid>

            <Actions>
              <ContinueButton disabled={!selectedId} onClick={onContinue}>
                Continue
              </ContinueButton>
            </Actions>
          </>
        )}
      </Wrapper>
    </StudentLayout>
  );
}
